import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Key, Plus, Trash, ArrowRight, ArrowLeft, Play, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { value: "exact", label: "Exact match" },
  { value: "numeric", label: "Numeric + tolerance" },
  { value: "fuzzy", label: "Fuzzy text match" },
];

function ColSelect({ columns, value, onChange, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="font-mono text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {columns.map((c) => (
          <SelectItem key={c} value={c} className="font-mono text-sm">{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function MappingStep({
  datasetA, datasetB, keyA, setKeyA, keyB, setKeyB,
  comparisons, setComparisons, onBack, onRun, running,
  compName, setCompName,
}) {
  const colsA = datasetA.columns;
  const colsB = datasetB.columns;
  const defaultName = `${datasetA.filename} vs ${datasetB.filename}`;

  const updateCmp = (i, patch) =>
    setComparisons(comparisons.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const addCmp = () =>
    setComparisons([
      ...comparisons,
      {
        _uid: (crypto.randomUUID?.() || String(Date.now() + Math.random())),
        name: `Comparison ${comparisons.length + 1}`,
        col_a: "", col_b: "", type: "exact",
        tolerance: 0, tol_mode: "abs", fuzzy_threshold: 90,
      },
    ]);

  const removeCmp = (i) => setComparisons(comparisons.filter((_, idx) => idx !== i));

  const handleRun = () => {
    if (!keyA || !keyB) return toast.error("Select the key column for both datasets");
    if (comparisons.length === 0) return toast.error("Add at least one comparison");
    for (const c of comparisons) {
      if (!c.col_a || !c.col_b) return toast.error(`Set both columns for "${c.name}"`);
    }
    onRun();
  };

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Step 2 / 3</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Map keys & comparisons</h2>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Columns don't need matching names. Pick the key that identifies the same record in each
          dataset, then pair the columns you want to compare.
        </p>
      </div>

      {/* Comparison name — distinct tinted label bar */}
      <div className="mb-8 bg-primary/[0.05] border border-primary/25 border-l-4 border-l-primary px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <PencilSimple size={18} weight="fill" className="text-primary" />
            <Label className="font-mono text-xs uppercase tracking-[0.2em] text-primary whitespace-nowrap">
              Comparison name
            </Label>
          </div>
          <Input
            data-testid="comparison-name-input"
            value={compName}
            onChange={(e) => setCompName(e.target.value)}
            placeholder={defaultName}
            className="bg-white flex-1"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          A memorable label for easy identification later. Leave blank to use “{defaultName}”.
        </p>
      </div>

      {/* Key mapping */}
      <div className="border border-border bg-white">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-secondary">
          <Key size={18} weight="fill" className="text-primary" />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Record Key (identity)</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5 p-5">
          <div className="space-y-1.5">
            <Label className="font-mono text-xs text-muted-foreground">Dataset A — {datasetA.filename} — key</Label>
            <ColSelect columns={colsA} value={keyA} onChange={setKeyA} placeholder="Select key column" testid="key-a-select" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-xs text-muted-foreground">Dataset B — {datasetB.filename} — key</Label>
            <ColSelect columns={colsB} value={keyB} onChange={setKeyB} placeholder="Select key column" testid="key-b-select" />
          </div>
        </div>
      </div>

      {/* Comparisons */}
      <div className="mt-8 space-y-4">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Column comparisons
          </span>
          <div className="flex-1 h-px bg-border" />
          <span className="font-mono text-xs text-muted-foreground">{comparisons.length} defined</span>
        </div>
        {comparisons.map((c, i) => (
          <div
            key={c._uid || i}
            data-testid={`comparison-${i}`}
            className="border border-border border-l-4 border-l-primary/70 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/60">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white font-mono text-xs shrink-0">
                  {i + 1}
                </span>
                <Input
                  data-testid={`comparison-${i}-name`}
                  value={c.name}
                  onChange={(e) => updateCmp(i, { name: e.target.value })}
                  className="max-w-xs h-8 font-semibold bg-white"
                />
              </div>
              <button
                data-testid={`comparison-${i}-remove`}
                onClick={() => removeCmp(i)}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash size={18} />
              </button>
            </div>
            <div className="p-5 grid gap-5 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="font-mono text-xs text-muted-foreground">Dataset A column</Label>
                <ColSelect columns={colsA} value={c.col_a} onChange={(v) => updateCmp(i, { col_a: v })} placeholder="Select column" testid={`comparison-${i}-col-a`} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono text-xs text-muted-foreground">Dataset B column</Label>
                <ColSelect columns={colsB} value={c.col_b} onChange={(v) => updateCmp(i, { col_b: v })} placeholder="Select column" testid={`comparison-${i}-col-b`} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono text-xs text-muted-foreground">Match rule</Label>
                <Select value={c.type} onValueChange={(v) => updateCmp(i, { type: v })}>
                  <SelectTrigger data-testid={`comparison-${i}-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {c.type === "numeric" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs text-muted-foreground">Tolerance</Label>
                    <Input
                      type="number"
                      data-testid={`comparison-${i}-tolerance`}
                      value={c.tolerance}
                      onChange={(e) => updateCmp(i, { tolerance: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs text-muted-foreground">Mode</Label>
                    <Select value={c.tol_mode} onValueChange={(v) => updateCmp(i, { tol_mode: v })}>
                      <SelectTrigger data-testid={`comparison-${i}-tolmode`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="abs">Absolute (±)</SelectItem>
                        <SelectItem value="pct">Percent (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {c.type === "fuzzy" && (
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs text-muted-foreground">Similarity threshold (0–100)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    data-testid={`comparison-${i}-fuzzy`}
                    value={c.fuzzy_threshold}
                    onChange={(e) => updateCmp(i, { fuzzy_threshold: parseInt(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addCmp}
          data-testid="add-comparison-button"
          className="w-full gap-2 border-dashed h-12"
        >
          <Plus size={18} /> Add comparison
        </Button>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="mapping-back-button" className="gap-2">
          <ArrowLeft size={18} /> Back
        </Button>
        <Button onClick={handleRun} disabled={running} data-testid="run-comparison-button" className="gap-2 h-11 px-6 group">
          {running ? "Comparing…" : "Run comparison"}
          {running ? null : <Play size={18} weight="fill" />}
        </Button>
      </div>
    </div>
  );
}
